import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import {
  fetchVendorProductData as catalogFetchVendorProductData,
  productsFormData as catalogProductsFormData,
  updateProductFormData as catalogUpdateProductFormData,
  postAddProductImage as catalogPostAddProductImage,
  duplicateItemData as catalogDuplicateItemData,
  combineProductData,
} from '../../../../api/catalog';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import { addItemToAnOrderFromCatalog, getAddToOrderList } from '../../../../redux/slices/orders';
import {
  getProductDetails as catalogGetProductDetails,
  getCatalog as catalogGetCatalog,
} from '../../../../redux/slices/catalog';
import { ScopeGuard } from '../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';


NewItemDialog.propTypes = {
  parentInfo: PropTypes.object,
  data: PropTypes.array,
  row: PropTypes.object,
  addToOrderList: PropTypes.object,
  agoraCat: PropTypes.array,
  agoraSubCat: PropTypes.array,
  agoraVendors: PropTypes.array,
  buttonProps: PropTypes.object,
  orderId: PropTypes.string,
};

export default function NewItemDialog({
  parentInfo,
  data,
  row,
  addToOrderList = null,
  agoraCat,
  agoraSubCat,
  agoraVendors,
  buttonProps,
  orderId
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id = '' } = useParams();

  const [open, setOpen] = useState(false);

  const [vendor, setVendor] = useState(null);
  const [vendorId, setVendorId] = useState(null);
  const [uomType, setUomType] = useState(null);
  const [step, setStep] = useState(parentInfo ? 2 : 1);
  const [uomType2, setUomType2] = useState(null);
  const [uomTypes, setUomTypes] = useState(null);
  const [productData, setProductData] = useState(null);
  const [parentData, setParentData] = useState(null);
  const [additionalUOM, setAdditionalUOM] = useState(false);
  const [ppdOptions, setPpdOptions] = useState([]);
  const [categories, setCategories] = useState([
    {
      agoraCategoryId: addToOrderList?.categoryId || null,
      agoraSubcategoryId: null,
    },
  ]);
  const { enqueueSnackbar } = useSnackbar();
  const {
    data: { agoraCategories, agoraSubcategories, vendors },
  } = useSelector((state) => (state.catalog));
  const [allCategories, setAllCategories] = useState(null);
  const [allSubcategories, setAllSubCategories] = useState(null);
  const [allVendors, setAllVendors] = useState(null);

  useEffect(() => {
    if (addToOrderList) {
      setAllCategories(agoraCat);
      setAllSubCategories(agoraSubCat);
      setAllVendors(agoraVendors);
    } else {
      setAllCategories(agoraCategories);
      setAllSubCategories(agoraSubcategories);
      setAllVendors(vendors);
    }
  }, [agoraCategories]);
  const checkDuplicateItem = async (vendorId, vendorItemId) => {
    const response = await catalogDuplicateItemData(vendorId, vendorItemId);
    if (response.status === 200) {
      if (response.data === true) {
        enqueueSnackbar('Item already exists with same vendor and vendor id', { variant: 'error' });
        return false;
      }
    }
    return true;
  };
  useEffect(() => {
    if (parentInfo) {
      setParentData({
        description: parentInfo.description,
        notes: parentInfo.notes,
        files: parentInfo.files
      });
      setCategories(parentInfo.categories);
    }
  }, [parentInfo]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setVendor(null);
    setVendorId(null);
    setUomType(null);
    setUomTypes(null);
    setProductData(null);
    setAdditionalUOM(false);
    setStep(parentInfo ? 2 : 1);
    setOpen(false);
    setParentData(null);
    setCategories([
      {
        agoraCategoryId: addToOrderList?.categoryId || null,
        agoraSubcategoryId: null,
      },
    ]);
  };

  const addProduct = async () => {
    let picUrls = productData?.pictureUrls ? [...productData?.pictureUrls] : [];
    const processedUrls = await Promise.all(
      picUrls.map(async (url) => {
          try {
            if (!url.includes('//res.cloudinary.com/agoraserv')) {
              const imgUrl =  await catalogPostAddProductImage(url);
              return imgUrl?.data?.length ? imgUrl.data : null;
            }
            return url;
          } catch (err) {
            console.error("Upload failed for URL:", url, err);
            return null;
          }
      })
    );

    picUrls = processedUrls.filter((url) => url !== null);

    productData.pictureUrls = picUrls;
    productData.uomType = uomType.value;
    const response =  await catalogProductsFormData({
      parentData,
      productData,
      categories,
      vendorId,
      vendorValue: vendor?.value,
      ppdFacilities: ppdOptions,
    });
    if (response.status === 200) {
      const createdProductId = response?.data?.productId;
      if (!createdProductId) {
        enqueueSnackbar('Item was saved but no product ID was returned', { variant: 'warning' });
        return;
      }
      const combineTargetProductId = productData?.groupto;
      let combinedSuccessfully = false;

      if (combineTargetProductId) {
        try {
          const combineResponse = await combineProductData({
            connectTo: combineTargetProductId,
            connectProductIds: [createdProductId]
          });
          if (combineResponse.status === 200) {
            combinedSuccessfully = true;
            enqueueSnackbar('Item added and combined successfully', { variant: 'success' });
          } else {
            enqueueSnackbar('Item added but failed to combine', { variant: 'warning' });
          }
        } catch (error) {
          enqueueSnackbar('Item added but error combining products', { variant: 'warning' });
        }
      } else {
        enqueueSnackbar('Item added successfully', { variant: 'success' });
      }

      const detailProductId =
        combineTargetProductId && combinedSuccessfully ? combineTargetProductId : createdProductId;
      
      if (addToOrderList) {
        dispatch(getAddToOrderList(id, addToOrderList.facilityId, addToOrderList.categoryId));
      } else {
        dispatch(catalogGetCatalog());
      }
      
      if (row !== undefined) {
         dispatch(catalogGetProductDetails(row.productId, false));
      } else {
        dispatch(catalogGetProductDetails(orderId ? createdProductId : detailProductId, false, orderId ?
          (data) => dispatch(addItemToAnOrderFromCatalog(data, {
            agoraCategory: allCategories.find((cat) => cat.id === categories[0].agoraCategoryId),
            agoraSubcategory: allSubcategories.find((cat) => cat.id === categories[0].agoraSubcategoryId)
          }, orderId))
          : null));
      }
      if (!orderId) navigate(`/dashboard/admin/catalog/${detailProductId}`);
    } else enqueueSnackbar('Error Adding Item', { variant: 'error' });
    handleClose();
  };

  const urlPattern = /^(https?:\/\/|ftps?:\/\/)/;

  const saveProduct = async () => {
    let picUrls = productData.pictureUrls;
    if (picUrls) {
      const processedUrls = await Promise.all(
        picUrls.map(async (url) => {
          try {
            if (!url.includes('//res.cloudinary.com/agoraserv') && urlPattern.test(url)) {
              const imgUrl = await catalogPostAddProductImage(url);
              return imgUrl?.data?.length ? imgUrl.data : null;
            }
            return url;
          } catch (err) {
            console.error("Upload failed for URL:", url, err);
            return null;
          }
        })
      );
      picUrls = processedUrls.filter((url) => url !== null);
      productData.pictureUrls = picUrls;
    }

    const response =  await catalogUpdateProductFormData({
          categories,
          allItems: [
            ...data,
            {
              ...productData,
              vendorId: vendor?.value,
              uomType: uomType.value,
              vendorItemId: vendorId,
              pictures: picUrls,
              ppdFacilities: ppdOptions,
            },
          ],
          productId: parentInfo?.id,
          description: parentData?.description,
          notes: parentData?.notes,
          files: parentData?.files,
          agoraCategoryId: categories[0].agoraCategoryId,
          agoraSubcategoryId: categories[0].agoraSubcategoryId,
        });
    if (response.status === 200) {
      if (row !== undefined) {
        dispatch(
          catalogGetProductDetails(row.productId, false)
        );
        dispatch(catalogGetCatalog(id));
      } else {
        dispatch(catalogGetProductDetails(id, false));
      }
      enqueueSnackbar('Item added successfully', { variant: 'success' });
      handleClose();
    } else enqueueSnackbar('Error Saving Item', { variant: 'error' });
  };

  const getProductData = async () => {
    const isDuplicate = await checkDuplicateItem(vendor?.value, vendorId);
    if (!isDuplicate) return;

    if (vendor && vendorId && uomType) {
      const response =  await catalogFetchVendorProductData(vendor?.value, vendorId, uomType.value);
      const data = response;
      if (data.status === 200) {
        const obj = data.data;
        setProductData({ ...obj, manufacturerId: obj.manufacturerNumber, error: data.data?.message });
      } else {
        setProductData({
          error: `${vendor.label} does not have item info. Please add manually.`,
          uomAmount: '',
          price: '',
          baseUOMAmount: '',
          baseUOMType: '',
          manufacturer: '',
          manufacturerId: '',
          baseMinUOMAmount: '',
          id: '',
          pictureUrls: [],
          vendorItemLink: '',
          vendorItemDescription: '',
          uomType: uomType.value,
        });
      }
    }
    // The combine step only applies when creating a brand new item, not when
    // adding a vendor to an existing product.
    setStep(parentInfo ? 4 : 3);
  };

  return (
    <div>
        <ScopeGuard scopes={['Item-Catalog-Edit']} allowAdmin>
          <Button
            variant="contained"
            size="small"
            color="secondary"
            onClick={handleClickOpen}
            startIcon={<Iconify icon="eva:plus-outline" />}
            sx={{ mt: 1 }}
            {...buttonProps}
          >
            {parentInfo ? 'Add Vendor' : 'Add Item'}
          </Button>
        </ScopeGuard>
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            textAlign: 'center',
            mt: 2,
          }}
        >
          New item
          <Box sx={{ m: 2, mt: 3, mr: 14, ml: 14 }}>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
              {parentInfo ? (
                <>
                  <Box sx={{ width: '48%' }}>
                    <LinearProgress variant="determinate" value={100} sx={{ height: 12, borderRadius: 3 }} />
                  </Box>
                  <Box sx={{ width: '48%' }}>
                    <LinearProgress variant="determinate" value={step > 2 ? 100 : 0} sx={{ height: 12, borderRadius: 3 }} />
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ width: '23%' }}>
                    <LinearProgress variant="determinate" value={100} sx={{ height: 12, borderRadius: 3 }} />
                  </Box>
                  <Box sx={{ width: '23%' }}>
                    <LinearProgress variant="determinate" value={step > 1 ? 100 : 0} sx={{ height: 12, borderRadius: 3 }} />
                  </Box>
                  <Box sx={{ width: '23%' }}>
                    <LinearProgress variant="determinate" value={step > 2 ? 100 : 0} sx={{ height: 12, borderRadius: 3 }} />
                  </Box>
                  <Box sx={{ width: '23%' }}>
                    <LinearProgress variant="determinate" value={step > 3 ? 100 : 0} sx={{ height: 12, borderRadius: 3 }} />
                  </Box>
                </>
              )}
            </Grid>
          </Box>
          <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent sx={{ height: '100%', width: '100%', ...(step === 3 && { overflowY: 'hidden' }) }}>
          {step === 4 ? (
            <Step4
              productData={productData}
              setProductData={setProductData}
              additionalUOM={additionalUOM}
              setAdditionalUOM={setAdditionalUOM}
              uomType2={uomType2}
              setUomType2={setUomType2}
              uomTypes={uomTypes}
              vendor={vendor}
              vendorId={vendorId}
              uomType={uomType}
              ppdOptions={ ppdOptions}
              setPpdOptions={setPpdOptions}
              vendors={allVendors}
              parentData={parentData}
              categories={categories}
              canCombine={!parentInfo}
            />
          ) : step === 3 ? (
            <Step3
              parentData={parentData}
              productData={productData}
              setProductData={setProductData}
              categories={categories}
              vendor={vendor}
            />
          ) : step === 2 ? (
            <Step2
              vendor={vendor}
              setVendor={setVendor}
              vendorId={vendorId}
              setVendorId={setVendorId}
              uomType={uomType}
              setUomType={setUomType}
              uomTypes={uomTypes}
              setUomTypes={setUomTypes}
              setPpdOptions={setPpdOptions}
              vendors={allVendors}
            />
          ) : (
            <Step1
              parentData={parentData}
              setParentData={setParentData}
              categories={categories}
              setCategories={setCategories}
              agoraCategories={allCategories}
              agoraSubcategories={allSubcategories}
              agoraVendors={allVendors}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', mb: 2 }}>
          {step === 4 ? (
            <>
              <Button
                variant="outlined"
                color="primary"
                sx={{ width: '200px' }}
                disabled={!vendor || !vendorId || !uomType}
                onClick={() => setStep(parentInfo ? 2 : 3)}
              >
                Previous
              </Button>
              <Tooltip
                title={
                  vendor && vendorId && uomType && productData?.uomAmount && productData?.price && !additionalUOM
                    ? ''
                    : `
              ${!vendor ? 'Vendor Required' : ''} 
              ${!vendorId ? 'Vendor ID Required' : ''}
              ${uomType ? 'UOM Type Required' : ''}
              ${!productData?.uomAmount ? 'UOM Amount Required' : ''}
              ${!productData?.price ? 'Price Required' : ''}
              ${additionalUOM ? (!productData?.uomAmount2 ? 'UOM Amount Required' : '') : ''}
              ${additionalUOM ? (!productData?.price2 ? 'Price Required' : '') : ''}`
                }
                placement="top"
                // arrow
              >
                <Box>
                  <AwaitButton
                    variant="contained"
                    color="primary"
                    sx={{ width: '200px', boxShadow: 3, fontWeight: 'bold' }}
                    disabled={
                      !vendor ||
                      !vendorId ||
                      !uomType ||
                      !productData?.uomAmount ||
                      !productData?.price ||
                      additionalUOM
                        ? !productData?.uomAmount2 || !productData?.price2 || !uomType2
                        : false
                    }
                    onClick={parentInfo ? saveProduct : addProduct}
                  >
                    {!parentInfo && productData?.groupto ? 'Combine And Save Item' : 'Add Item'}
                  </AwaitButton>
                </Box>
              </Tooltip>
            </>
          ) : step === 3 ? (
            <>
              <Button
                variant="outlined"
                color="primary"
                sx={{ width: '200px' }}
                onClick={() => setStep(2)}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ minWidth: '200px', px: 3, whiteSpace: 'nowrap', boxShadow: 3, fontWeight: 'bold' }}
                onClick={() => setStep(4)}
              >
                {productData?.groupto ? 'Continue with this Match' : 'Next'}
              </Button>
            </>
          ) : step === 2 ? (
            <>
              <Button
                variant="outlined"
                color="primary"
                sx={{ width: '200px' }}
                onClick={() => {
                  setVendor(null);
                  setVendorId(null);
                  setUomType(null);
                  setUomTypes(null);
                  setStep(1);
                }}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ width: '200px', boxShadow: 3, fontWeight: 'bold' }}
                disabled={!vendor || !vendorId || !uomType || vendorId.length > 300}
                onClick={() => {
                  getProductData();
                }}
              >
                Next
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              sx={{ width: '200px', boxShadow: 3, fontWeight: 'bold' }}
              onClick={() => setStep(2)}
              disabled={
                !parentData?.description ||
                categories.some((category) => !category.agoraCategoryId || !category.agoraSubcategoryId)
              }
            >
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
